import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SnackbarService } from '../../../shared/services/snackbar.service';
import { Subject, takeUntil } from 'rxjs';
import { DeadLetterDetail } from '../../models/dead-letter-detail.model';
import { AppInsightsLogEntry } from '../../models/dead-letter-logs.model';
import { DeadLetterDetailSnapshot } from '../../models/dead-letter-snapshot.model';
import { DeadLetterCompareService, DisplayComparedField } from '../../services/dead-letter-compare.service';
import { DeadLetterService } from '../../services/dead-letter.service';

export interface DeadLetterDetailDialogData {
  id: string;
  snapshot?: DeadLetterDetailSnapshot;
}

@Component({
    selector: 'app-dead-letter-detail-dialog',
    templateUrl: './dead-letter-detail-dialog.component.html',
    styleUrls: ['./dead-letter-detail-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DeadLetterDetailDialogComponent implements OnInit, OnDestroy {
  // Detail state
  detail: DeadLetterDetail | null = null;
  rawJson = '';
  detailLoading = false;
  detailError = false;
  notFound = false;

  // Logs state
  logsEntries: AppInsightsLogEntry[] = [];
  logsLoading = false;
  logsError = false;

  // Compare state
  companyCompare: Record<string, DisplayComparedField> = {};
  treatmentGroupCompare: Record<string, DisplayComparedField> = {};

  // Section expand state
  overviewExpanded = true;
  companyExpanded = false;
  diagnosticsExpanded = true;
  treatmentGroupExpanded = false;

  private readonly destroy$ = new Subject<void>();
  private requestGeneration = 0;

  constructor(
    @Inject(MAT_DIALOG_DATA) private readonly data: DeadLetterDetailDialogData,
    private readonly dialogRef: MatDialogRef<DeadLetterDetailDialogComponent>,
    private readonly deadLetterService: DeadLetterService,
    private readonly snackbarService: SnackbarService,
    readonly compareService: DeadLetterCompareService,
    private readonly changeDetectorRef: ChangeDetectorRef
  ) {}

  get deadLetterId(): string {
    return this.data?.id ?? '';
  }

  get snapshot(): DeadLetterDetailSnapshot | undefined {
    return this.data?.snapshot;
  }

  ngOnInit(): void {
    this.load();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  close(): void {
    this.dialogRef.close();
  }

  retry(): void {
    this.load();
  }

  resubmit(): void {
    this.deadLetterService.replayDeadLetters({ ids: [this.deadLetterId] }).subscribe({
      next: (result) => {
        if (result.failed > 0) {
          this.showActionError();
          return;
        }
        this.dialogRef.close('resubmitted');
      },
      error: () => this.showActionError()
    });
  }

  delete(): void {
    this.deadLetterService.removeDeadLetters({ ids: [this.deadLetterId] }).subscribe({
      next: (result) => this.dialogRef.close({ removed: result.removed }),
      error: () => this.showActionError()
    });
  }

  private showActionError(): void {
    this.snackbarService.error('DEAD_LETTERS.ERRORS.ACTION_FAILED');
  }

  private load(): void {
    if (!this.deadLetterId) {
      this.detailLoading = false;
      this.detailError = false;
      this.notFound = true;
      this.detail = null;
      this.rawJson = '';
      this.logsEntries = [];
      this.logsLoading = false;
      this.logsError = false;
      return;
    }

    const generation = ++this.requestGeneration;
    this.resetState();
    this.loadDetail(generation);
    this.loadLogs(generation);
  }

  private resetState(): void {
    this.detailLoading = true;
    this.detailError = false;
    this.notFound = false;
    this.detail = null;
    this.rawJson = '';
    this.companyCompare = {};
    this.treatmentGroupCompare = {};
    this.overviewExpanded = true;
    this.companyExpanded = false;
    this.diagnosticsExpanded = true;
    this.treatmentGroupExpanded = false;
    this.logsEntries = [];
    this.logsLoading = true;
    this.logsError = false;
  }

  private loadDetail(generation: number): void {
    this.deadLetterService.getDeadLetter(this.deadLetterId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (detail) => {
          if (generation !== this.requestGeneration) {
            return;
          }

          this.detailLoading = false;

          if (!detail) {
            this.notFound = true;
            this.changeDetectorRef.detectChanges();
            return;
          }

          this.detail = detail;
          this.rawJson = this.formatJson(detail.dataJson) || JSON.stringify(detail, null, 2);
          this.buildCompareState(detail);
          this.changeDetectorRef.detectChanges();
        },
        error: () => {
          if (generation !== this.requestGeneration) {
            return;
          }

          this.detailLoading = false;
          this.detailError = true;
          this.changeDetectorRef.detectChanges();
        }
      });
  }

  private loadLogs(generation: number): void {
    this.deadLetterService.getDeadLetterLogs(this.deadLetterId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (logs) => {
          if (generation !== this.requestGeneration) {
            return;
          }

          this.logsLoading = false;
          this.logsEntries = logs || [];
          this.changeDetectorRef.detectChanges();
        },
        error: () => {
          if (generation !== this.requestGeneration) {
            return;
          }

          this.logsLoading = false;
          this.logsError = true;
          this.changeDetectorRef.detectChanges();
        }
      });
  }

  private buildCompareState(detail: DeadLetterDetail): void {
    const result = this.compareService.buildCompareState(detail, this.snapshot);
    this.companyCompare = result.company;
    this.treatmentGroupCompare = result.treatmentGroup;
  }

  private formatJson(json: string): string {
    if (!json) {
      return '';
    }
    try {
      return JSON.stringify(JSON.parse(json), null, 2);
    } catch {
      return json;
    }
  }
}
